import { LoggerService } from '@/services/abstract/LoggerService';
import { LogLevel, type Logger, type LoggerConfig, type LoggerOptions } from '@/types/Logger';

export class LoggerServiceImpl extends LoggerService {
  private globalConfig: Required<LoggerConfig>;
  private enabledPatterns: string[] = [];
  private disabledPatterns: string[] = [];
  private readonly loggers = new Map<string, ConsoleLogger>();

  private readonly levelColors: Record<LogLevel, string> = {
    [LogLevel.ERROR]: '#ff4444',
    [LogLevel.WARN]: '#ffaa00',
    [LogLevel.INFO]: '#00aaff',
    [LogLevel.DEBUG]: '#00ff00',
    [LogLevel.TRACE]: '#888888',
  };

  private readonly levelHierarchy: Record<LogLevel, number> = {
    [LogLevel.ERROR]: 0,
    [LogLevel.WARN]: 1,
    [LogLevel.INFO]: 2,
    [LogLevel.DEBUG]: 3,
    [LogLevel.TRACE]: 4,
  };

  constructor() {
    super();
    this.globalConfig = {
      level: LogLevel.INFO,
      enabled: '*',
      includeTimestamp: true,
      formatTimestamp: () => new Date().toISOString(),
    };
    this.initializeFromEnvironment();
  }

  createLogger(options: LoggerOptions): Logger {
    const namespace = options.namespace;
    this.refreshFromStorage();

    const existingLogger = this.loggers.get(namespace);
    if (existingLogger !== undefined) {
      return existingLogger;
    }

    const logger = new ConsoleLogger({
      namespace,
      level: options.level ?? this.globalConfig.level,
      enabled: options.enabled ?? this.isNamespaceEnabled(namespace),
      color: options.color,
      config: this.globalConfig,
      levelColors: this.levelColors,
      levelHierarchy: this.levelHierarchy,
    });

    this.loggers.set(namespace, logger);
    return logger;
  }

  configure(config: LoggerConfig): void {
    this.globalConfig = {
      ...this.globalConfig,
      ...config,
    };

    if (config.enabled !== undefined) {
      this.enabledPatterns = config.enabled.split(',').map((pattern) => pattern.trim());
      this.disabledPatterns = [];
    }

    this.loggers.forEach((logger, namespace) => {
      logger.updateConfig(
        config.level ?? logger.getLevel(),
        this.isNamespaceEnabled(namespace)
      );
    });
  }

  enable(pattern: string): void {
    if (!this.enabledPatterns.includes(pattern)) {
      this.enabledPatterns.push(pattern);
    }

    this.disabledPatterns = this.disabledPatterns.filter(
      (disabledPattern) => disabledPattern !== pattern
    );
    this.updateMatchingLoggers(pattern, true);
  }

  disable(pattern: string): void {
    if (!this.disabledPatterns.includes(pattern)) {
      this.disabledPatterns.push(pattern);
    }

    this.enabledPatterns = this.enabledPatterns.filter(
      (enabledPattern) => enabledPattern !== pattern
    );
    this.updateMatchingLoggers(pattern, false);
  }

  disableAll(): void {
    this.enabledPatterns = [];
    this.disabledPatterns = ['*'];
    this.loggers.forEach((logger) => {
      logger.disable();
    });
  }

  refreshFromStorage(): void {
    try {
      const debugPattern = globalThis.localStorage.getItem('DEBUG');
      if (debugPattern != null && debugPattern.length > 0) {
        const newPatterns = debugPattern.split(',').map((pattern) => pattern.trim());
        if (JSON.stringify(newPatterns) !== JSON.stringify(this.enabledPatterns)) {
          this.enabledPatterns = newPatterns;
          this.globalConfig.enabled = debugPattern;
          this.loggers.forEach((logger, namespace) => {
            logger.updateConfig(logger.getLevel(), this.isNamespaceEnabled(namespace));
          });
        }
      }

      const logLevel = globalThis.localStorage.getItem('LOG_LEVEL');
      if (logLevel != null && isValidLogLevel(logLevel) && logLevel !== this.globalConfig.level) {
        this.globalConfig.level = logLevel;
        this.loggers.forEach((logger) => {
          logger.updateConfig(logLevel, logger.isEnabled());
        });
      }
    } catch {
      // localStorage is not always available
    }
  }

  private initializeFromEnvironment(): void {
    try {
      let debugPattern = globalThis.localStorage.getItem('DEBUG');
      let logLevel = globalThis.localStorage.getItem('LOG_LEVEL');
      const envDebugPattern: unknown = import.meta.env.VITE_DEBUG_NAMESPACES;
      const envLogLevel: unknown = import.meta.env.VITE_LOG_LEVEL;

      if (debugPattern == null || debugPattern.length === 0) {
        debugPattern = typeof envDebugPattern === 'string' ? envDebugPattern : null;
      }

      logLevel ??= typeof envLogLevel === 'string' ? envLogLevel : null;

      if (debugPattern != null && debugPattern.length > 0) {
        this.enabledPatterns = debugPattern.split(',').map((pattern) => pattern.trim());
        this.globalConfig.enabled = debugPattern;
      }

      if (logLevel != null && isValidLogLevel(logLevel)) {
        this.globalConfig.level = logLevel;
      }
    } catch {
      // env/localStorage not available
    }
  }

  private isNamespaceEnabled(namespace: string): boolean {
    for (const pattern of this.disabledPatterns) {
      if (LoggerServiceImpl.matchPattern(namespace, pattern)) {
        return false;
      }
    }

    for (const pattern of this.enabledPatterns) {
      if (LoggerServiceImpl.matchPattern(namespace, pattern)) {
        return true;
      }
    }

    return this.enabledPatterns.length === 0 || this.enabledPatterns.includes('*');
  }

  private updateMatchingLoggers(pattern: string, enabled: boolean): void {
    this.loggers.forEach((logger, namespace) => {
      if (LoggerServiceImpl.matchPattern(namespace, pattern)) {
        if (enabled) {
          logger.enable();
        } else {
          logger.disable();
        }
      }
    });
  }

  private static matchPattern(namespace: string, pattern: string): boolean {
    if (pattern === '*') {
      return true;
    }

    const regexPattern = pattern
      .replace(/[.+?^${}()|[\]\\]/g, String.raw`\$&`)
      .replace(/\*/g, '.*');

    return new RegExp(`^${regexPattern}$`).test(namespace);
  }
}

interface ConsoleLoggerConfig {
  namespace: string;
  level: LogLevel;
  enabled: boolean;
  color?: string;
  config?: Required<LoggerConfig>;
  levelColors?: Record<LogLevel, string>;
  levelHierarchy?: Record<LogLevel, number>;
}

class ConsoleLogger implements Logger {
  private enabled: boolean;
  private level: LogLevel;
  private readonly namespace: string;
  private readonly color?: string;
  private readonly config?: Required<LoggerConfig>;
  private readonly levelColors?: Record<LogLevel, string>;
  private readonly levelHierarchy?: Record<LogLevel, number>;

  constructor(config: ConsoleLoggerConfig) {
    this.namespace = config.namespace;
    this.level = config.level;
    this.enabled = config.enabled;
    this.color = config.color;
    this.config = config.config;
    this.levelColors = config.levelColors;
    this.levelHierarchy = config.levelHierarchy;
  }

  error(...args: unknown[]): void {
    this.log(LogLevel.ERROR, args);
  }

  warn(...args: unknown[]): void {
    this.log(LogLevel.WARN, args);
  }

  info(...args: unknown[]): void {
    this.log(LogLevel.INFO, args);
  }

  debug(...args: unknown[]): void {
    this.log(LogLevel.DEBUG, args);
  }

  trace(...args: unknown[]): void {
    this.log(LogLevel.TRACE, args);
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getNamespace(): string {
    return this.namespace;
  }

  getLevel(): LogLevel {
    return this.level;
  }

  updateConfig(level: LogLevel, enabled: boolean): void {
    this.level = level;
    this.enabled = enabled;
  }

  private log(level: LogLevel, args: unknown[]): void {
    if (!this.enabled) {
      return;
    }

    if (
      this.levelHierarchy != null &&
      this.levelHierarchy[level] > this.levelHierarchy[this.level]
    ) {
      return;
    }

    const prefix = this.formatPrefix(level);
    const color = this.color ?? this.levelColors?.[level] ?? '#888888';

    /* eslint-disable no-console -- Logger intentionally writes to the console */
    const consoleMethod = console[level];
    consoleMethod(`%c${prefix}`, `color: ${color}; font-weight: bold;`, ...args);
    /* eslint-enable no-console */
  }

  private formatPrefix(level: LogLevel): string {
    const parts: string[] = [];

    if (this.config?.includeTimestamp === true) {
      parts.push(this.config.formatTimestamp());
    }

    parts.push(`[${level.toUpperCase()}]`, `[${this.namespace}]`);
    return parts.join(' ');
  }
}

function isValidLogLevel(value: string): value is LogLevel {
  return (Object.values(LogLevel) as string[]).includes(value);
}
